import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatTabsModule } from '@angular/material/tabs'
import { ActivatedRoute, Router } from '@angular/router'
import { map } from 'rxjs'
import { DatePipe } from '@angular/common'
import { MeetingsService, MeetingStatus, ClientMeeting, DeletedClientMeeting, MeetingReportingStatus } from '../meetings.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import {
  UndoSoftDeletionDialogComponent,
  UndoSoftDeletionDialogData,
  UndoSoftDeletionDialogResult,
} from '../meeting/undo-soft-deletion-dialog/undo-soft-deletion-dialog.component'
import { DurationPipe } from '../duration.pipe'
import { queryKeys } from '../query-keys'
import {
  AppTableChip,
  AppTableChipVariant,
  AppTableColumn,
  AppTableRowAction,
  AppTableRowActionEvent,
  AppTableSource,
  TableComponent,
} from '../table/table.component'
import { AvatarComponent } from '../avatar/avatar.component'
import { MatTooltip } from '@angular/material/tooltip'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../utils'
import {
  ExtendRetentionDialogComponent,
  ExtendRetentionDialogData,
  ExtendRetentionDialogResult,
} from './extend-retention-dialog/extend-retention-dialog.component'
import { MeetingTimelineDialogComponent, MeetingTimelineDialogData } from '../meeting-timeline-dialog/meeting-timeline-dialog.component'
import { ManageMeetingOwnersDialogComponent, ManageMeetingOwnersDialogData } from './manage-meeting-owners-dialog/manage-meeting-owners-dialog.component'
import {
  RetentionExtensionHistoryDialogComponent,
  RetentionExtensionHistoryDialogData,
} from './retention-extension-history-dialog/retention-extension-history-dialog.component'

const DEFAULT_PAGE_SIZE = 25

const TAB_NAMES = ['active', 'deleted'] as const

const STATUS_CHIP_VARIANTS: Record<MeetingReportingStatus, AppTableChipVariant> = {
  SCHEDULED: 'neutral',
  NOT_JOINING: 'neutral',
  RECORDING: 'warning',
  PROCESSING: 'warning',
  RECORDED: 'success',
  NOT_RECORDED: 'danger',
  CANCELLED: 'neutral',
  SOFT_DELETED: 'danger',
}

// A meeting's retention period starts once it has finished, so before then any deletion date it
// carries says nothing about when it will go.
const UNFINISHED_STATUSES: MeetingReportingStatus[] = ['SCHEDULED', 'NOT_JOINING', 'RECORDING', 'PROCESSING']

/** A deletion date as the page shows it: grey while it is only scheduled, orange once it has happened. */
export type DeletionDateCell = { date: Date | null; label: string; hasHappened: boolean; note: string }

@Component({
  selector: 'app-admin-meetings-page',
  standalone: true,
  imports: [ToolbarComponent, TableComponent, TranslatePipe, TranslateDirective, AvatarComponent, MatTooltip, MatTabsModule],
  providers: [DatePipe, DurationPipe],
  templateUrl: './admin-meetings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-meetings-page.component.scss',
})
export class AdminMeetingsPageComponent implements OnInit {
  private meetingsService = inject(MeetingsService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)
  private datePipe = inject(DatePipe)
  private durationPipe = inject(DurationPipe)
  private route = inject(ActivatedRoute)
  private router = inject(Router)

  defaultPageSize = DEFAULT_PAGE_SIZE

  // Newest first within whatever window is in force, so the most recent meeting is the first row.
  readonly defaultSort = { columnId: 'date', direction: 'desc' as const }
  private localTimeZone = resolveLocalTimeZone()

  @ViewChild('nameCell', { static: true }) nameCell!: TemplateRef<{ $implicit: ClientMeeting }>
  @ViewChild('meetingOwnersCell', { static: true }) meetingOwnersCell!: TemplateRef<{ $implicit: ClientMeeting }>
  // The same cell, typed for the deleted meetings' rows, whose owners have the same shape.
  @ViewChild('meetingOwnersCell', { static: true }) deletedMeetingOwnersCell!: TemplateRef<{ $implicit: DeletedClientMeeting }>
  @ViewChild('softDeletionCell', { static: true }) softDeletionCell!: TemplateRef<{ $implicit: ClientMeeting }>
  @ViewChild('hardDeletionCell', { static: true }) hardDeletionCell!: TemplateRef<{ $implicit: ClientMeeting }>

  /** Meetings a new assistant has been sent to in this sitting, so the action cannot be repeated. */
  private meetingIdsSentARecoveryAssistant = new Set<number>()

  private extendRetentionPeriodMutation = this.meetingsService.extendRetentionPeriodMutation()
  private restoreMeetingMutation = this.meetingsService.restoreMeetingMutation()
  private sendRecoveryAssistantMutation = this.meetingsService.sendRecoveryAssistantMutation()

  /**
   * Every status rather than the joinable ones alone: the table's own status filter is what narrows
   * the list now, and it can only offer what the endpoint returns.
   *
   * Keyed on the mutations' own query key, so extending a retention period or restoring a meeting
   * refreshes the table.
   */
  readonly source: AppTableSource<ClientMeeting> = {
    url: '/api/meetings/client',
    params: { includeAllStatuses: true },
    queryKey: queryKeys.meetings.client.base,
  }

  rowId = (meeting: ClientMeeting): string => String(meeting.id)

  columns: AppTableColumn<ClientMeeting>[] = []

  // Kept in the URL so that a link to the deleted meetings opens on them.
  selectedTabIndex = Math.max(0, TAB_NAMES.indexOf(this.route.snapshot.queryParamMap.get('tab') as (typeof TAB_NAMES)[number]))

  readonly deletedSource: AppTableSource<DeletedClientMeeting> = {
    url: '/api/meetings/client/deleted',
    queryKey: [...queryKeys.meetings.client.base, 'deleted'],
  }

  readonly deletedDefaultSort = { columnId: 'deletedAt', direction: 'desc' as const }

  deletedRowId = (meeting: DeletedClientMeeting): string => String(meeting.id)

  deletedColumns: AppTableColumn<DeletedClientMeeting>[] = []

  selectTab(index: number) {
    this.selectedTabIndex = index
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: TAB_NAMES[index] }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  ngOnInit() {
    this.columns = [
      { id: 'id', header: 'COMMON.ID', value: (meeting) => meeting.id, numeric: true, tone: 'muted', sortable: true, filter: 'text' },
      // The name is drawn by hand so a soft-deleted meeting can be struck through. Sorting, filtering
      // and the CSV all read `value`, so none of them are affected by how the cell is drawn.
      {
        id: 'name',
        header: 'COMMON.NAME',
        value: (meeting) => meeting.name,
        cellTemplate: this.nameCell,
        tone: 'strong',
        sortable: true,
        filter: 'text',
        maskClass: 'mask-meeting-name',
      },
      {
        id: 'date',
        header: 'COMMON.DATE_AND_TIME',
        // Acting on a meeting that has happened is the common errand, so that is where the list opens.
        defaultDatePreset: 'past',
        value: (meeting) => new Date(meeting.date).getTime(),
        display: (meeting) => this.formatDate(meeting.date),
        sortable: true,
        filter: 'date',
        autoFit: true,
      },
      {
        id: 'duration',
        header: 'COMMON.DURATION',
        value: (meeting) => meeting.duration ?? 0,
        display: (meeting) => this.formatDuration(meeting.duration),
        numeric: true,
      },
      {
        id: 'users',
        header: 'ADMIN.MEETINGS.OWNERS',
        // The names are what the column sorts and downloads by; the avatars are how it reads.
        value: (meeting) => meeting.users.map((user) => `${user.firstName} ${user.lastName}`).join(', '),
        cellTemplate: this.meetingOwnersCell,
        filter: 'text',
        autoFit: true,
      },
      {
        id: 'status',
        header: 'COMMON.STATUS',
        value: (meeting) => this.describeStatus(meeting).label,
        chip: (meeting) => this.describeStatus(meeting),
        filter: 'facet',
        // The values arrive as the server names them — RECORDED and the like — and the reader knows
        // them by the words the status column itself shows.
        facetLabel: (value) => this.translateService.instant(`ADMIN.MEETINGS.REPORTING_STATUSES.${value}`),
      },
      {
        id: 'softDeletionDate',
        header: 'ADMIN.MEETINGS.SOFT_DELETION',
        value: (meeting) => this.timestampOf(this.softDeletionOf(meeting).date),
        display: (meeting) => this.softDeletionOf(meeting).label,
        cellTemplate: this.softDeletionCell,
        sortable: true,
        autoFit: true,
      },
      {
        id: 'deletionDate',
        // Blank for a team that keeps its meetings, since then there is no such date.
        header: 'ADMIN.MEETINGS.HARD_DELETION_DATE',
        value: (meeting) => this.timestampOf(this.hardDeletionOf(meeting).date),
        display: (meeting) => this.hardDeletionOf(meeting).label,
        cellTemplate: this.hardDeletionCell,
        sortable: true,
        autoFit: true,
      },
    ]
    this.deletedColumns = [
      { id: 'id', header: 'COMMON.ID', value: (meeting) => meeting.id, numeric: true, tone: 'muted', sortable: true, filter: 'text' },
      { id: 'name', header: 'COMMON.NAME', value: (meeting) => meeting.name, tone: 'strong', sortable: true, filter: 'text', maskClass: 'mask-meeting-name' },
      {
        id: 'date',
        header: 'COMMON.DATE_AND_TIME',
        value: (meeting) => new Date(meeting.date).getTime(),
        display: (meeting) => this.formatDate(meeting.date),
        sortable: true,
        filter: 'date',
        autoFit: true,
      },
      {
        id: 'duration',
        header: 'COMMON.DURATION',
        value: (meeting) => meeting.duration ?? 0,
        display: (meeting) => this.formatDuration(meeting.duration),
        numeric: true,
        sortable: true,
      },
      {
        id: 'users',
        header: 'ADMIN.MEETINGS.OWNERS',
        value: (meeting) => meeting.users.map((user) => `${user.firstName} ${user.lastName}`).join(', '),
        cellTemplate: this.deletedMeetingOwnersCell,
        filter: 'text',
        autoFit: true,
      },
      {
        id: 'deletedAt',
        header: 'ADMIN.MEETINGS.DELETED_AT',
        value: (meeting) => new Date(meeting.deletedAt).getTime(),
        display: (meeting) => this.formatDate(meeting.deletedAt),
        sortable: true,
        filter: 'date',
        autoFit: true,
      },
    ]
  }

  readonly rowActions: AppTableRowAction<ClientMeeting>[] = [
    {
      id: 'extendRetention',
      label: 'MEETINGS.EXTEND_RETENTION.BUTTON',
      icon: 'more_time',
      isVisible: (meeting) => meeting.canExtendRetentionPeriod && meeting.markedToBeDeletedAt === null,
    },
    {
      id: 'retentionHistory',
      label: 'MEETINGS.EXTEND_RETENTION.HISTORY_BUTTON',
      icon: 'history',
      isVisible: (meeting) => meeting.retentionExtensionCount > 0,
    },
    // Reading what has happened to a meeting is not acting on it, so it is offered whatever the
    // team has decided about the actions below.
    { id: 'timeline', label: 'MEETINGS.ADMIN.SHOW_TIMELINE', icon: 'timeline' },
    {
      id: 'undoSoftDeletion',
      label: 'MEETINGS.ADMIN.SOFT_DELETION.UNMARK_TITLE',
      icon: 'restore_from_trash',
      isVisible: (meeting) => meeting.canActOnMeeting && meeting.markedToBeDeletedAt !== null,
    },
    { id: 'manageUsers', label: 'MEETINGS.ADMIN.MANAGE_MEETING_OWNERS.BUTTON', icon: 'manage_accounts', isVisible: (meeting) => meeting.canActOnMeeting },
    {
      id: 'sendRecoveryAssistant',
      label: 'ADMIN.MEETINGS.SEND_NEW_ASSISTANT',
      disabledLabel: 'ADMIN.MEETINGS.SEND_NEW_ASSISTANT_ALREADY_SENT',
      icon: 'smart_toy',
      isVisible: (meeting) => meeting.canActOnMeeting && meeting.isEligibleForRecoveryAssistant,
      isDisabled: (meeting) => this.meetingIdsSentARecoveryAssistant.has(meeting.id),
    },
  ]

  handleRowAction({ actionId, row }: AppTableRowActionEvent<ClientMeeting>) {
    const handlers: Record<string, (meeting: ClientMeeting) => void> = {
      extendRetention: (meeting) => this.extendRetentionPeriod(meeting),
      retentionHistory: (meeting) => this.showRetentionExtensionHistory(meeting),
      timeline: (meeting) => this.showTimeline(meeting),
      undoSoftDeletion: (meeting) => this.undoSoftDeletion(meeting),
      manageUsers: (meeting) => this.manageMeetingOwners(meeting),
      sendRecoveryAssistant: (meeting) => this.sendRecoveryAssistant(meeting),
    }
    handlers[actionId]?.(row)
  }

  /**
   * Says what the strikethrough means, for the reader who wonders rather than the one who knows.
   *
   * Empty for a meeting that is not soft-deleted, so the tooltip only appears where there is
   * something to explain.
   */
  softDeletionNote(meeting: ClientMeeting): string {
    return meeting.markedToBeDeletedAt === null ? '' : this.translateService.instant('ADMIN.MEETINGS.SOFT_DELETED_NAME_NOTE')
  }

  // The page has read in the reader's own zone since the retention dialogs moved to it, so a date in
  // the table cannot go back to London's.
  private formatDate(value: Date | null): string {
    return value === null ? '-' : (this.datePipe.transform(value, DATE_TIME_FORMAT, undefined, undefined) ?? '-')
  }

  private timestampOf(value: Date | null): number {
    return value === null ? 0 : new Date(value).getTime()
  }

  /**
   * When the meeting is hidden from its team: the day it was, once it has been, and the day
   * it is due to be until then.
   */
  softDeletionOf(meeting: ClientMeeting): DeletionDateCell {
    if (meeting.markedToBeDeletedAt !== null) {
      return this.deletionDateCell(meeting.markedToBeDeletedAt, true, 'ADMIN.MEETINGS.SOFT_DELETION_HAPPENED')
    }
    return this.deletionDateCell(this.unlessUnfinished(meeting, meeting.softDeletionDate), false, 'ADMIN.MEETINGS.DELETION_SCHEDULED')
  }

  /** Always a scheduled date: a meeting that has been destroyed is no longer on the list to show it. */
  hardDeletionOf(meeting: ClientMeeting): DeletionDateCell {
    return this.deletionDateCell(this.unlessUnfinished(meeting, meeting.deletionDate), false, 'ADMIN.MEETINGS.DELETION_SCHEDULED')
  }

  private unlessUnfinished(meeting: ClientMeeting, date: Date | null): Date | null {
    return UNFINISHED_STATUSES.includes(meeting.reportingStatus) ? null : date
  }

  private deletionDateCell(date: Date | null, hasHappened: boolean, noteKey: string): DeletionDateCell {
    const label = this.formatDate(date)
    return { date, label, hasHappened, note: date === null ? '' : this.translateService.instant(noteKey, { date: label }) }
  }

  private formatDuration(value: number | null): string {
    return value === null ? '-' : String(this.durationPipe.transform(value) ?? '-')
  }

  /**
   * The status the server reports, which is also what the status filter matches on, so the chip and
   * the filter can never disagree.
   */
  private describeStatus(meeting: ClientMeeting): AppTableChip {
    return {
      label: this.translateService.instant(`ADMIN.MEETINGS.REPORTING_STATUSES.${meeting.reportingStatus}`),
      variant: STATUS_CHIP_VARIANTS[meeting.reportingStatus],
    }
  }

  private extendRetentionPeriod(meeting: ClientMeeting) {
    // The dialog is drawn from both of the meeting's deletion dates, so one that is missing either of
    // them has no timeline to show.
    if (meeting.deletionDate === null || meeting.softDeletionDate === null) {
      return
    }
    const data: ExtendRetentionDialogData = {
      meetingName: meeting.name,
      meetingDate: meeting.date,
      currentSoftDeletionDate: meeting.softDeletionDate,
      currentDeletionDate: meeting.deletionDate,
      maximumDeletionDate: meeting.maximumDeletionDate,
      maximumSoftDeletionDate: meeting.maximumSoftDeletionDate,
      maximumSoftDeletionDateInTotal: meeting.maximumSoftDeletionDateInTotal,
      maxSingleExtensionInDays: meeting.maxSingleExtensionInDays,
      maxTotalExtensionInDays: meeting.maxTotalExtensionInDays,
      usedTotalExtensionInDays: meeting.usedTotalExtensionInDays,
      minimumSoftDeletionDate: meeting.minimumSoftDeletionDate,
      minimumDeletionDate: meeting.minimumDeletionDate,
    }
    const dialogRef = this.dialog.open<ExtendRetentionDialogComponent, ExtendRetentionDialogData, ExtendRetentionDialogResult | null>(
      ExtendRetentionDialogComponent,
      { width: '640px', data }
    )
    // Dismissing the dialog — escape, the backdrop, Cancel or the cross — closes it with nothing.
    dialogRef
      .afterClosed()
      .pipe(map((result) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.extendRetentionPeriodMutation.mutateAsync({
            meetingId: meeting.id,
            deletionDate: result.deletionDate,
            softDeletionDate: result.softDeletionDate,
          })
        }
      })
  }

  private showTimeline(meeting: ClientMeeting) {
    const data: MeetingTimelineDialogData = { id: meeting.id }
    this.dialog.open(MeetingTimelineDialogComponent, { width: '900px', maxHeight: '80vh', data, panelClass: 'timeline-dialog' })
  }

  private showRetentionExtensionHistory(meeting: ClientMeeting) {
    const data: RetentionExtensionHistoryDialogData = { meetingId: meeting.id, meetingName: meeting.name }
    this.dialog.open(RetentionExtensionHistoryDialogComponent, { width: '960px', maxWidth: '95vw', data })
  }

  private undoSoftDeletion(meeting: ClientMeeting) {
    const data: UndoSoftDeletionDialogData = {
      meetingId: meeting.id,
      currentSoftDeletionDate: meeting.softDeletionDate,
      currentDeletionDate: meeting.deletionDate,
      fixedDeletionDate: meeting.deletionDateKeptByRestore,
    }
    const dialogRef = this.dialog.open<UndoSoftDeletionDialogComponent, UndoSoftDeletionDialogData, UndoSoftDeletionDialogResult | null>(
      UndoSoftDeletionDialogComponent,
      { width: '500px', data }
    )
    dialogRef
      .afterClosed()
      .pipe(map((result) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.restoreMeetingMutation.mutateAsync({ meetingId: meeting.id, dates: result.dates })
        }
      })
  }

  private manageMeetingOwners(meeting: ClientMeeting) {
    const data: ManageMeetingOwnersDialogData = {
      meetingId: meeting.id,
      meetingName: meeting.name,
      access: meeting.access,
      allowRestrictedUsers: meeting.allowRestrictedUsers,
    }
    this.dialog.open(ManageMeetingOwnersDialogComponent, { width: '560px', data, autoFocus: 'first-heading' })
  }

  private sendRecoveryAssistant(meeting: ClientMeeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('ADMIN.MEETINGS.SEND_NEW_ASSISTANT'),
        description: this.translateService.instant(
          meeting.status === MeetingStatus.TIMEOUT || meeting.status === MeetingStatus.ERRORED
            ? 'ADMIN.MEETINGS.SEND_NEW_ASSISTANT_TO_UNRECORDED_DESCRIPTION'
            : 'MEETINGS.ADMIN.SEND_RECOVERY_ASSISTANT.DESCRIPTION'
        ),
      },
    })
    dialogRef
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true))
      .subscribe(async (confirmed) => {
        if (confirmed) {
          // Marked before the request goes out, so a second click cannot land while the first is in
          // flight. A rejected request releases the lock again: the endpoint refuses a duplicate on
          // its own, because a meeting that was sent one is left waiting for a retry.
          this.meetingIdsSentARecoveryAssistant.add(meeting.id)
          try {
            await this.sendRecoveryAssistantMutation.mutateAsync(meeting.id)
          } catch {
            this.meetingIdsSentARecoveryAssistant.delete(meeting.id)
          }
        }
      })
  }
}
